import React, { useState } from 'react'
import { Button } from 'components/ui/button'
import { usePartnerApplications } from 'hooks/usePartnerAdmin'
import PartnersLayout from './PartnersLayout'
import ReviewApplicationModal from './ReviewApplicationModal'
import { formatDate, shortId } from './format'
import type { Application, ApplicationStatus } from './types'

const th =
  'text-left text-xs font-semibold uppercase text-neutral-500 px-3 py-2'
const td = 'px-3 py-2 text-sm border-t border-neutral-100'
const STATUSES: ApplicationStatus[] = ['pending', 'approved', 'rejected']

const ApplicationsQueue: React.FC = () => {
  const [status, setStatus] = useState<ApplicationStatus>('pending')
  const { data, error, isLoading, mutate } = usePartnerApplications(status)
  const [reviewing, setReviewing] = useState<Application | null>(null)
  const apps = data ?? []

  return (
    <PartnersLayout
      title="Applications"
      actions={
        <select
          className="border border-neutral-300 rounded-md px-3 py-2 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value as ApplicationStatus)}
          aria-label="Status filter"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      }
    >
      {error && (
        <div className="text-sm text-red-600 mb-3">
          Failed to load: {(error as Error).message}
        </div>
      )}
      {isLoading && <div className="text-sm text-neutral-600">Loading…</div>}
      {!isLoading && apps.length === 0 && !error && (
        <div className="text-sm text-neutral-600">
          No {status} applications.
        </div>
      )}
      {apps.length > 0 && (
        <div className="overflow-x-auto border border-neutral-200 rounded-lg">
          <table className="min-w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className={th}>Submitted</th>
                <th className={th}>Handle</th>
                <th className={th}>User</th>
                <th className={th}>X</th>
                <th className={th}>Telegram</th>
                <th className={th}>Audience</th>
                <th className={th}>Reviewed by</th>
                <th className={th}></th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id}>
                  <td className={td}>{formatDate(a.created_at)}</td>
                  <td className={td}>{a.handle}</td>
                  <td className={td} title={a.privy_id}>
                    {shortId(a.privy_id)}
                  </td>
                  <td className={td}>{a.x_handle || '—'}</td>
                  <td className={td}>{a.telegram_handle || '—'}</td>
                  <td className={td}>{a.audience_size}</td>
                  <td className={td}>{a.reviewed_by ?? '—'}</td>
                  <td className={td}>
                    {a.status === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setReviewing(a)}
                      >
                        Review
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ReviewApplicationModal
        application={reviewing}
        onClose={() => setReviewing(null)}
        onReviewed={() => mutate()}
        onError={() => mutate()}
      />
    </PartnersLayout>
  )
}

export default ApplicationsQueue
